<script setup>
/**
 * The AI confirm (P-5.12), IN PLACE: the same price, balance, tick, Confirm and Cancel as
 * `AiConfirmDialog.vue`, from the same `useAiConfirm` controller and the same strings, drawn
 * under the button that asked instead of in an overlay.
 *
 * WHY A SECOND FACE FOR ONE CONFIRM. The task composer imports no overlay: `Dialog` reaches
 * the store through `TeleportWithDirection` and throws inside a render effect after a unit test
 * has resolved, which can only fail an exit code (`specs/task-compose-screens.spec.js`, "the
 * composer imports no overlay"). Every Suggest in the composer, the menu editor and
 * Personality asks here instead. What the owner is asked, and what the server charges, are
 * identical: nothing here decides a number.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** What `useAiConfirm` returns. */
  controller: { type: Object, required: true },
});

const { t } = useWasloI18n();
const state = computed(() => props.controller.dialog);

const phrase = value =>
  value === null || value === undefined
    ? ''
    : props.controller.creditsText(value);

const body = computed(() =>
  t('WASLO.AI_CONFIRM.BODY', {
    credits: phrase(state.value.credits),
    balance: phrase(state.value.balance),
  })
);

const changed = computed(() =>
  state.value.priceChanged
    ? t('WASLO.AI_CONFIRM.PRICE_CHANGED', {
        credits: phrase(state.value.credits),
      })
    : ''
);
</script>

<template>
  <section
    v-if="state.open"
    class="flex flex-col gap-3 p-3 rounded-lg outline outline-1 outline-n-weak bg-n-alpha-1"
    data-waslo-ai-confirm
  >
    <p class="mb-0 text-sm font-medium text-n-slate-12">
      {{ t('WASLO.AI_CONFIRM.TITLE') }}
    </p>
    <p
      v-if="changed"
      class="mb-0 text-sm font-medium text-n-slate-12"
      data-waslo-ai-confirm-changed
    >
      {{ changed }}
    </p>
    <p class="mb-0 text-sm text-n-slate-12" data-waslo-ai-confirm-body>
      {{ body }}
    </p>
    <label
      class="flex items-start gap-3 text-sm cursor-pointer text-n-slate-12"
    >
      <span class="mt-0.5 shrink-0">
        <Checkbox
          :model-value="state.ticked"
          data-waslo-ai-confirm-tick
          @update:model-value="controller.setTicked"
        />
      </span>
      <span>{{ t('WASLO.AI_CONFIRM.TICK') }}</span>
    </label>
    <div class="flex gap-2">
      <Button
        :label="t('WASLO.AI_CONFIRM.CONFIRM')"
        size="sm"
        :disabled="!state.ticked"
        data-waslo-ai-confirm-yes
        @click="controller.confirm()"
      />
      <Button
        :label="t('WASLO.AI_CONFIRM.CANCEL')"
        size="sm"
        variant="faded"
        color="slate"
        data-waslo-ai-confirm-no
        @click="controller.cancel()"
      />
    </div>
  </section>
</template>
